import { defineStore } from 'pinia'
import { sharedGetToken } from '@shared/utils/helper'
import { ZERO_IN_BIG_NUMBER } from '@shared/utils/constant'
import { toUiMarketHistory } from '@shared/transformer/market'
import { getExchangeApi, getIndexerRestMarketChronosApi } from '@shared/service'
import type { SharedUiMarketHistory } from '@shared/types'
import type {
  TokenStatic,
  ExchangeParams,
  FeeDiscountSchedule,
  FeeDiscountAccountInfo
} from '@injectivelabs/sdk-ts'
import type { TradingRewardsCampaign } from '@/app/client/types/exchange'

type ExchangeStoreState = {
  params?: ExchangeParams
  tradeRewardsPoints: string[]
  pendingTradeRewardsPoints: string[]
  marketsHistory: SharedUiMarketHistory[]
  feeDiscountSchedule?: FeeDiscountSchedule
  feeDiscountAccountInfo?: FeeDiscountAccountInfo
  tradingRewardsCampaign?: TradingRewardsCampaign
}

const initialStateFactory = (): ExchangeStoreState => ({
  params: undefined,
  feeDiscountSchedule: undefined,
  feeDiscountAccountInfo: undefined,
  tradingRewardsCampaign: undefined,
  tradeRewardsPoints: [],
  pendingTradeRewardsPoints: [],
  marketsHistory: []
})

export const useExchangeStore = defineStore('exchange', {
  state: (): ExchangeStoreState => initialStateFactory(),

  actions: {
    async initFeeDiscounts() {
      const exchangeStore = useExchangeStore()

      await exchangeStore.fetchFeeDiscountAccountInfo()
    },

    async initTradeAndEarn() {
      const exchangeStore = useExchangeStore()

      await exchangeStore.fetchTradeRewardPoints()
      await exchangeStore.fetchPendingTradeRewardPoints()
    },

    async fetchParams() {
      const exchangeApi = await getExchangeApi()

      const exchangeStore = useExchangeStore()

      exchangeStore.params = await exchangeApi.fetchModuleParams()
    },

    async fetchFeeDiscountSchedule() {
      const exchangeApi = await getExchangeApi()

      const exchangeStore = useExchangeStore()

      const feeDiscountSchedule = await exchangeApi.fetchFeeDiscountSchedule()

      if (feeDiscountSchedule) {
        const quoteTokenMeta = (await Promise.all(
          feeDiscountSchedule.quoteDenomsList.map(
            async (denom) => await sharedGetToken(denom)
          )
        )) as TokenStatic[]

        const feeDiscountScheduleWithToken = {
          ...feeDiscountSchedule,
          quoteTokenMeta
        } as FeeDiscountSchedule

        exchangeStore.$patch({
          feeDiscountSchedule: {
            ...feeDiscountScheduleWithToken,
            tierInfosList: [
              {
                volume: '0',
                stakedAmount: '0',
                makerDiscountRate: '0',
                takerDiscountRate: '0'
              },
              ...feeDiscountScheduleWithToken.tierInfosList
            ]
          }
        })
      }
    },

    async fetchFeeDiscountAccountInfo() {
      const exchangeApi = await getExchangeApi()

      const exchangeStore = useExchangeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const feeDiscountAccountInfo =
        await exchangeApi.fetchFeeDiscountAccountInfo(
          sharedWalletStore.authZOrInjectiveAddress
        )

      if (feeDiscountAccountInfo) {
        exchangeStore.feeDiscountAccountInfo = feeDiscountAccountInfo
      }
    },

    async fetchTradingRewardsCampaign() {
      const exchangeApi = await getExchangeApi()

      const exchangeStore = useExchangeStore()

      const tradingRewardsCampaign =
        await exchangeApi.fetchTradingRewardsCampaign()

      if (!tradingRewardsCampaign) {
        return
      }

      const quoteDenomsList = tradingRewardsCampaign.tradingRewardCampaignInfo
        ? tradingRewardsCampaign.tradingRewardCampaignInfo.quoteDenomsList
        : []
      const quoteSymbolsList = (
        (
          await Promise.all(
            quoteDenomsList.map(async (denom) => await sharedGetToken(denom))
          )
        ).filter((token) => token) as TokenStatic[]
      ).map((token) => token.symbol)

      const tradingRewardCampaignInfo = {
        ...tradingRewardsCampaign.tradingRewardCampaignInfo,
        quoteSymbolsList
      }
      const tradingRewardsCampaignWithToken = {
        ...tradingRewardsCampaign,
        tradingRewardCampaignInfo
      } as TradingRewardsCampaign

      exchangeStore.tradingRewardsCampaign = tradingRewardsCampaignWithToken
    },

    async fetchTradeRewardPoints() {
      const exchangeApi = await getExchangeApi()

      const exchangeStore = useExchangeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      exchangeStore.tradeRewardsPoints =
        await exchangeApi.fetchTradeRewardPoints([
          sharedWalletStore.authZOrInjectiveAddress
        ])
    },

    async fetchPendingTradeRewardPoints() {
      const exchangeApi = await getExchangeApi()

      const exchangeStore = useExchangeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const { params, tradingRewardsCampaign } = exchangeStore

      if (!params || !tradingRewardsCampaign) {
        return
      }

      const pendingRewardsList =
        tradingRewardsCampaign.pendingTradingRewardPoolCampaignScheduleList

      if (pendingRewardsList.length === 0) {
        return
      }

      const rewards = await Promise.all(
        pendingRewardsList.map(async (pendingReward) => {
          const rewards = await exchangeApi.fetchPendingTradeRewardPoints(
            [sharedWalletStore.authZOrInjectiveAddress],
            pendingReward.startTimestamp
          )

          return rewards
            .reduce((total, reward) => {
              return total.plus(reward)
            }, ZERO_IN_BIG_NUMBER)
            .toFixed()
        })
      )

      exchangeStore.pendingTradeRewardsPoints = rewards
    },

    async fetchMarketHistory({
      marketIds,
      resolution,
      countback
    }: {
      countback: number
      resolution: number
      marketIds: string[]
    }) {
      const exchangeStore = useExchangeStore()
      const indexerRestMarketChronosApi = await getIndexerRestMarketChronosApi()

      const marketHistoryAlreadyExists = marketIds.every((marketId) => {
        return exchangeStore.marketsHistory.find(
          (marketHistory: SharedUiMarketHistory) => {
            return marketHistory.marketId === marketId
          }
        )
      })

      if (marketHistoryAlreadyExists) {
        return
      }

      try {
        const marketsHistory =
          await indexerRestMarketChronosApi.fetchMarketsHistory({
            marketIds,
            resolution,
            countback
          })

        const marketsHistoryToUiMarketsHistory =
          marketsHistory.map(toUiMarketHistory)

        exchangeStore.$patch({
          marketsHistory: [
            ...exchangeStore.marketsHistory,
            ...marketsHistoryToUiMarketsHistory
          ]
        })
      } catch {
        // don't do anything for now
      }
    },

    async fetchMarketsHistory({
      marketIds,
      resolution,
      countback
    }: {
      countback: number
      resolution: number
      marketIds: string[]
    }) {
      const indexerRestMarketChronosApi = await getIndexerRestMarketChronosApi()

      const exchangeStore = useExchangeStore()

      if (exchangeStore.marketsHistory.length > 0 || marketIds.length === 0) {
        return
      }

      try {
        const marketsHistory =
          await indexerRestMarketChronosApi.fetchMarketsHistory({
            marketIds,
            resolution,
            countback
          })

        const marketsHistoryToUiMarketsHistory =
          marketsHistory.map(toUiMarketHistory)

        exchangeStore.$patch({
          marketsHistory: [
            ...exchangeStore.marketsHistory,
            ...marketsHistoryToUiMarketsHistory
          ]
        })
      } catch {
        // don't do anything for now
      }
    },

    async fetchMarketHistoryNew({
      marketIds,
      resolution,
      countback
    }: {
      countback: number
      resolution: number
      marketIds: string[]
    }) {
      const indexerRestMarketChronosApi = await getIndexerRestMarketChronosApi()

      const exchangeStore = useExchangeStore()

      try {
        const marketsHistory =
          await indexerRestMarketChronosApi.fetchMarketsHistory({
            marketIds,
            resolution,
            countback
          })

        const marketsHistoryToUiMarketsHistory =
          marketsHistory.map(toUiMarketHistory)

        exchangeStore.$patch({
          marketsHistory: [...marketsHistoryToUiMarketsHistory]
        })
      } catch {
        // don't do anything for now
      }
    },

    reset() {
      const exchangeStore = useExchangeStore()

      const {
        tradeRewardsPoints,
        feeDiscountSchedule,
        feeDiscountAccountInfo,
        tradingRewardsCampaign,
        pendingTradeRewardsPoints
      } = initialStateFactory()

      exchangeStore.$patch({
        tradeRewardsPoints,
        feeDiscountSchedule,
        feeDiscountAccountInfo,
        tradingRewardsCampaign,
        pendingTradeRewardsPoints
      })
    }
  }
})
